import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import DynamicHeadTag from "components/__new/common/DynamicHeadTag";
import { ButtonLoader } from "components/__new/common/form";
import { ReactComponent as IconAngleLeft } from "assets/images/icons/project-icons/angle-left.svg";
import ComplianceHeader from "components/__new/compliance/ComplianceHeader";
import useBackButton from "hooks/useBackButton";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import DirectorInformation from "components/__new/compliance/directorInformation";
import useLoginInfo from "hooks/useLoginInfo";
import useUploadDocument from "hooks/compliance/useUploadDocument";
import validate from "utils/validate";
import PageContent from "components/__new/common/PageContent";
import Maybe from "components/__new/common/Maybe";
import * as complianceActions from "store/entities/compliance/action";
import * as accountActions from "store/auth/account/action";


const VerifyDirector = () => {
    const history = useHistory();
    const {goBack} = useBackButton();
    const dispatch = useDispatch();
    const {userInfo} = useLoginInfo();
    let successResponse = useRef(0);
    const { uploadFile, uploadLoading } = useUploadDocument();
    const { directorsAddedLoading, directorsAdded, directorsUpdatedLoading, directorsUpdated, directorsDeletedLoading, directorsDeleted } = useSelector((s) => s.entities.compliance);
    const { employerInfoLoading, employerInfo, profileUpdateLoading, profileUpdated } = useSelector((s) => s.auth.account);
    
    
    const [directorType, setDirectorType] = useState({
        label: "director",
        minimumDirectors: 2,
    });
    const [exitAfterSave, setExitAfterSave] = useState(true);
    const [isFormValidated, setIsFormValidated] = useState(false);
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const [directorsToAddCount, setDirectorsToAddCount] = useState(0);
    const [directorsToUpdateCount, setDirectorsToUpdateCount] = useState(0);
    const [directorToDelete, setDirectorToDelete] = useState(null);
    const [directorsDuplicated, setDirectorsDuplicated] = useState(false);
    const [directors, setDirectors] = useState([]);
    const directorSample = {
        _id: "",
        directorFirstName: "",
        directorLastName: "",
        directorDob: "",
        nationality: "",
        documentType: "",
        documentFile: "",
        bvn: "",
    }
    
    
    const addDirector = () => {
        const data = [...directors, directorSample];
        setDirectors(data);
        setIsFormValidated(false);
    }
    
    
    const updateDirector = (index, form) => {
        const data = directors.map((director, position) => {
            if (index === position){
                return {
                    ...director,
                    ...form,
                }
            }
            else{
                return director;
            }
        })
        setDirectors(data);
    }
    
    
    const deleteDirector = (id, index) => {
        if (!id){
            const newDirectors = [...directors];
            newDirectors.splice(index, 1);
            setDirectors(newDirectors);
            checkFormValidation(newDirectors);
        }
        else{
            toast("Deleting Director " + (index + 1));
            setDirectorToDelete(index);
            dispatch(complianceActions.deleteDirectors(id));
        }
    }
    
    
    const getLoadingStatus = () => {
        if (uploadLoading === true || directorsAddedLoading === true || directorsUpdatedLoading === true || directorsDeletedLoading === true || profileUpdateLoading === true){
            return true;
        }
        return false;
    }
    
    
    const checkFormValidation = (data = directors, appendKey, appendValue, index) => {
        let errorResponses = 0;
        let bvns = [];
        let bvnDuplicated = false;
        if (data.length > 0){
            for (let i = 0; i < data.length; i++){
                let params = {...data[i]};
                delete params["_id"];
                
                if (appendKey && index !== undefined && index === i){
                    params = {...params, [appendKey]: appendValue};
                }
                
                const errors = validate(params);
                if (errors){
                    errorResponses++;
                    // console.log("checkFormValidation", errors);
                }
                
                if (params["bvn"] && params["bvn"] !== ""){
                    if (bvns.indexOf(params["bvn"]) === -1){
                        bvns.push(params["bvn"]);
                    }
                    else{
                        bvnDuplicated = true;
                    }
                }
            }
        }
        setDirectorsDuplicated(bvnDuplicated);
        setIsFormValidated(errorResponses === 0 ? true : false);
    }
    
    
    const saveDraft = () => {
        submitForm(undefined, false);
    }
    
    
    const submitForm = async (e, exit=true) => {
        e?.preventDefault();
        setIsFormSubmitted(true);
        setExitAfterSave(exit);
        
        let updatedDirectors = [...directors];
        if (updatedDirectors.length > 0){
            updatedDirectors = await doFileUploads(updatedDirectors);
            
            const directorsToAdd = updatedDirectors.filter((director) => director._id === "");
            doAddDirectors(directorsToAdd)
            
            const directorsToUpdate = updatedDirectors.filter((director) => director._id !== "" && director._id !== undefined);
            doUpdateDirectors(directorsToUpdate)
        }
    }
    
    
    const doFileUploads = async (updatedDirectors) => {
        for (let i = 0; i < updatedDirectors.length; i++){
            const file = updatedDirectors[i].documentFile;
            const name = updatedDirectors[i].documentType;
            if (file !== "" && typeof file !== "string" && file?.lastModified !== undefined){
                const response = await uploadFile(file, name, userInfo?.company?.companyName);
                if (response?.status === "success"){
                    updatedDirectors = updatedDirectors.map((director, position) => {
                        if (i === position && response?.url){
                            return {
                                ...director,
                                documentFile: response?.url ?? "",
                            }
                        }
                        else{
                            return director;
                        }
                    })
                }
                else{
                    toast.error(file?.name + " could not be uploaded");
                }
            }
        }
        return updatedDirectors;
    }
    
    
    const doAddDirectors = (directorsToAdd) => {
        const count = directorsToAdd.length;
        if (count > 0){
            setDirectorsToAddCount(count);
            const params = directorsToAdd.map((director) => {
                delete director["_id"];
                return director;
            })
            dispatch(complianceActions.addDirectors(params));
        }
    }
    
    
    const doUpdateDirectors = (directorsToUpdate) => {
        const count = directorsToUpdate.length;
        if (count > 0){
            setDirectorsToUpdateCount(count);
            dispatch(complianceActions.startUpdateDirectors());
            for (let i = 0; i < count; i++){
                const params = directorsToUpdate[i];
                const id = params["_id"];
                delete params["_id"];
                dispatch(complianceActions.updateDirectors(id, params));
            }
        }
    }
    
    
    const getEmployerInfo = () => {
        if(!employerInfoLoading && (Object.keys(employerInfo).length === 0 || employerInfo.status !== "success")){
            dispatch(accountActions.getEmployerInfo())
        }
    }
    
    
    const populateDirectors = (company) => {
        const businessDirectors = company?.businessDirectors ?? [];
        const type = getDirectorType(company?.registrationType) ?? {};
        if (businessDirectors.length > 0){
            const populatedDirectors = businessDirectors.map((businessDirector) => {
                return {
                    _id: businessDirector?._id ?? "",
                    directorFirstName: businessDirector?.directorFirstName ?? "",
                    directorLastName: businessDirector?.directorLastName ?? "",
                    directorDob: businessDirector?.directorDob ?? "",
                    nationality: businessDirector?.nationality ?? "",
                    documentType: businessDirector?.documentType ?? "",
                    documentFile: businessDirector?.documentFile ?? "",
                    bvn: businessDirector?.bvn ?? "",
                }
            })
            setDirectors(populatedDirectors);
            checkFormValidation(populatedDirectors);
        }
        else{
            const directorSampleTotal = [...Array(type.minimumDirectors ?? 1).keys()].map(() => directorSample);
            setDirectors(directorSampleTotal);
            checkFormValidation([directorSample]);
        }
    }
    
    
    const getDirectorType = (type) => {
        const types = {
            "limited liability": {
                label: "director",
                minimumDirectors: 2,
            },
            "ngo": {
                label: "trustee",
                minimumDirectors: 2,
            },
            "partnership": {
                label: "partner",
                minimumDirectors: 1,
            },
            "sole proprietorship": {
                label: "proprietor",
                minimumDirectors: 1,
            },
            "corporation": {
                label: "director",
                minimumDirectors: 2,
            },
        }
        if (types[type] !== undefined){
            setDirectorType(types[type]);
        }
        return types[type];
    }
    
    
    useEffect(() => {
        const {isCompliant, status} = userInfo.employer;

        if (isCompliant === true){
            history.push("/compliance/approval/success");
        }
        else if (isCompliant === false && status === "review"){
            history.push("/compliance/registered/approval");
        }
        else{
            dispatch(complianceActions.updateComplianceProgress(50));
            getEmployerInfo();
        }
        // eslint-disable-next-line
    }, [])
    
    
    useEffect(() => {
        if (employerInfoLoading === false && employerInfo.status === "success"){
            const company = employerInfo?.data?.employer?.company ?? {};
            populateDirectors(company);
        }
        // eslint-disable-next-line
    }, [employerInfoLoading, employerInfo])
    
    
    useEffect(() => {
        if (isFormSubmitted === true){
            if (directorsToAddCount > 0){
                if (directorsAdded.status === "success"){
                    successResponse.current += directorsToAddCount;
                    dispatch(complianceActions.resetAddDirectors());
                }
            }
        }
        // eslint-disable-next-line
    }, [directorsAdded])
    
    
    useEffect(() => {
        if (isFormSubmitted === true){
            if (directorsToUpdateCount > 0){
                if (directorsToUpdateCount === directorsUpdated.length){
                    for (let i in directorsUpdated){
                        if (directorsUpdated[i].status === "success"){
                            successResponse.current += 1;
                        }
                    }
                    dispatch(complianceActions.resetUpdateDirectors());
                }
            }
        }
        // eslint-disable-next-line
    }, [directorsUpdated])
    
    
    useEffect(() => {
        const index = directorToDelete;
        setDirectorToDelete(null);
        if (directorsDeleted.status === "success"){
            toast.success("Director " + (index + 1) + " deleted successfully");
            dispatch(complianceActions.resetDeleteDirectors());
            dispatch(accountActions.updateEmployerInfoFE(directorsDeleted?.data?.company));
        }
        // eslint-disable-next-line
    }, [directorsDeleted])
    
    
    useEffect(() => {
        if (isFormSubmitted === true){
            if (successResponse.current === (directorsToAddCount + directorsToUpdateCount)){
                setDirectorsToAddCount(0);
                setDirectorsToUpdateCount(0);
                setIsFormSubmitted(false);
                
                dispatch(accountActions.updateProfile({
                    firstName: userInfo?.employer?.firstName ?? "",
                    lastName: userInfo?.employer?.lastName ?? "",
                    phoneNumber: userInfo?.employer?.phoneNumber ?? "",
                    jobTitle: userInfo?.employer?.jobTitle ?? "",
                    email: userInfo?.employer?.email ?? "",
                    status: "review",
                }));
            }
        }
        // eslint-disable-next-line
    }, [successResponse.current])
    
    
    useEffect(() => {
        if (profileUpdated?.status === "success"){
            dispatch(accountActions.resetUpdateProfile());
            dispatch(accountActions.getEmployerInfo());
            if (exitAfterSave === true){
                history.push("/compliance/registered/approval");
            }
            else{
                toast.success("Your data has been saved");
            }
        }
        // eslint-disable-next-line
    }, [profileUpdated])
    
    
    return (
        <div>
            
            <DynamicHeadTag
                headerText="Earnipay Compliance"
                headerDescription="Complete each step to access all Earnipay features"
            />
            
            <ComplianceHeader />
            
            <div className="pt-6 px-6 pb-24 sm:pt-10 sm:px-10">
                <div className="max-w-2xl mx-auto box">
                    
                    <div className="flex justify-start">
                        <div onClick={goBack} className="w-max h-8 pr-2 mr-1 flex items-center cursor-pointer">
                            <IconAngleLeft style={{ height: "16px" }} className="w-max pr-3 m-auto" />
                            Back
                        </div>
                    </div>
                    
                    <div className="pb-2 mt-8 border-b">
                        <div className="font-bold text-xl capitalize">
                            {directorType?.label}’s Verfication
                        </div>
                        <div className="text-fade text-sm">
                            Please fill in the details to verify your {directorType?.label}
                        </div>
                    </div>
                    
                    <PageContent
                        loading={employerInfoLoading}
                        data={employerInfo}
                        loadingText="Fetching business information..."
                        emptyText="Business information not found"
                        errorText="Unable to fetch business information"
                    />

                    <Maybe condition={employerInfoLoading === false && employerInfo?.status === "success"}>
                        <form onSubmit={submitForm} className="mt-8">
                            {directors.map((director, index) =>
                                <DirectorInformation
                                    key={index}
                                    index={index}
                                    director={director}
                                    directorType={directorType}
                                    checkFormValidation={(docs, name, value) => checkFormValidation(docs, name, value, index)}
                                    updateDirector={(e) => updateDirector(index, e)}
                                    directorToDelete={directorToDelete}
                                    deleteDirector={deleteDirector}
                                />
                            )}
                            
                            <div onClick={addDirector} className="btn text-ep-primary capitalize hover:bg-gray-100">
                                <FontAwesomeIcon icon="plus" className="mr-2" />
                                Add {directorType?.label}
                            </div>
                            
                            <Maybe condition={directorsDuplicated === true}>
                                <div className="mt-4 form-input-message px-6 py-3 bg-red-100 rounded-sm">
                                    Duplicate {directorType?.label}s found. Each directors must have unique BVN.
                                </div>
                            </Maybe>
                            
                            <div className="mt-8 flex justify-end space-x-4">
                                <ButtonLoader type="button" onClick={saveDraft} loading={getLoadingStatus() === true && exitAfterSave === false} className="btn btn-transparent-primary" disabled={!isFormValidated || directorsDuplicated}>
                                    Save a Draft
                                </ButtonLoader>
                                <ButtonLoader type="submit" loading={getLoadingStatus() === true && exitAfterSave === true} className="btn btn-ep-primary" disabled={!isFormValidated || directorsDuplicated}>
                                    Continue
                                </ButtonLoader>
                            </div>
                        </form>
                    </Maybe>
                    
                </div>
            </div>
            
        </div>
    )
}


export default VerifyDirector;